import React from 'react';
import { Alert } from '@amzn/awsui-components-react';
import { ReportRunType, ReportVersion } from 'src/models/reports/ReportVersions';
import { BANNER_MESSAGES } from './tpTaxSectionConstants';

type ComponentProps = {
    // Whether the user has selected an accounting period yet.
    periodSelected: boolean;
    // Whether the versions for the selected period are still loading.
    loading: boolean;
    // The versions available for the selected period ([] when none exist yet).
    versions: ReportVersion[];
    // The version the user has selected (undefined when none / none selected).
    selectedVersion?: ReportVersion;
    // Whether the selected period is a fiscal quarter-end-close period.
    isQuarterEndPeriod: boolean;
};

/**
 * Renders the contextual banner above the TP Tax report cards, matching the Product mockup:
 *  - no reports for the selected period   -> warning banner
 *  - selected version is a VAR + PLI run  -> info banner (MAR/CWB/LEF not updated)
 *  - quarter-end-close period (full run)  -> info banner (Day 7 + Day 11 available)
 * Pure function of its props; renders nothing when none of the states apply.
 */
export default function ReportContextBanner(props: ComponentProps) {
    // Before a period is chosen (or while its versions are loading) there is nothing to say — the
    // "no reports" warning must only appear once a period is selected and confirmed to have none.
    if (!props.periodSelected || props.loading) {
        return null;
    }

    if (props.versions.length === 0) {
        return (
            <Alert type="warning" data-testid="report-context-banner">
                {BANNER_MESSAGES.NO_REPORTS}
            </Alert>
        );
    }

    if (props.selectedVersion?.runType === ReportRunType.VAR_PLI) {
        return (
            <Alert type="info" data-testid="report-context-banner">
                {BANNER_MESSAGES.VAR_PLI_ONLY}
            </Alert>
        );
    }

    if (props.isQuarterEndPeriod) {
        return (
            <Alert type="info" data-testid="report-context-banner">
                {BANNER_MESSAGES.QUARTER_END}
            </Alert>
        );
    }

    return null;
}
